/**
 * Cliente do backend do módulo de Inovação.
 *
 * Reusa o axios instance `api` com interceptor JWT e normalização de erros
 * já configurados no services/api.js.
 */

import api from './api';

const BASE = '/inovacao';

function unwrap(promise) {
  return promise.then((r) => r.data);
}

// ---------------- Análises (privado) ----------------

export const analisesAPI = {
  listar: (params = {}) =>
    unwrap(api.get(`${BASE}/analises`, { params })),
  obter: (id) => unwrap(api.get(`${BASE}/analises/${id}`)),
  criar: (body) => unwrap(api.post(`${BASE}/analises`, body)),
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/analises/${id}`, body)),
  deletar: (id) => unwrap(api.delete(`${BASE}/analises/${id}`)),
  convidar: (id, body = {}) =>
    unwrap(api.post(`${BASE}/analises/${id}/convidar`, body)),
  reenviarConvite: (id) =>
    unwrap(api.post(`${BASE}/analises/${id}/reenviar-convite`)),
  pedirAjuste: (id, comentario) =>
    unwrap(api.post(`${BASE}/analises/${id}/pedir-ajuste`, { comentario })),
  rejeitar: (id, motivo) =>
    unwrap(api.post(`${BASE}/analises/${id}/rejeitar`, { motivo })),
  moverBacklog: (id, motivo = null) =>
    unwrap(api.post(`${BASE}/analises/${id}/mover-para-backlog`, { motivo })),
  reabrir: (id) => unwrap(api.post(`${BASE}/analises/${id}/reabrir`)),
  marcarAprovadaTemp: (id) =>
    unwrap(api.post(`${BASE}/analises/${id}/marcar-aprovada-temp`)),
  // Questionário de esforço na tela compartilhada (por ID) — operador responde
  respostasEsforco: (id) =>
    unwrap(api.get(`${BASE}/analises/${id}/respostas-esforco`)),
  salvarRascunhoEsforco: (id, respostas) =>
    unwrap(api.put(`${BASE}/analises/${id}/respostas-esforco`, respostas)),
  submeterEsforco: (id, respostas) =>
    unwrap(api.post(`${BASE}/analises/${id}/submeter-esforco`, respostas)),
};

// ---------------- Análise pública (solicitante via token) ----------------

export const analisePublicaAPI = {
  obter: (token) =>
    unwrap(api.get(`${BASE}/publico/analise/${token}`)),
  salvarRascunho: (token, respostas) =>
    unwrap(api.put(`${BASE}/publico/analise/${token}/respostas`, respostas)),
  submeter: (token, respostas) =>
    unwrap(api.post(`${BASE}/publico/analise/${token}/submeter`, respostas)),
};

// ---------------- Análise Comparativa ----------------

export const comparativaAPI = {
  listar: (analiseId) =>
    unwrap(api.get(`${BASE}/analises/${analiseId}/comparativa`)),
  substituir: (analiseId, linhas) =>
    unwrap(api.put(`${BASE}/analises/${analiseId}/comparativa`, { linhas })),
  respostasOperador: (analiseId) =>
    unwrap(api.get(`${BASE}/analises/${analiseId}/respostas`)),
};

// ---------------- Aprovadores + aprovações ----------------

export const aprovacoesAPI = {
  minhasPendentes: () => unwrap(api.get(`${BASE}/minhas-aprovacoes`)),
  listarAprovadores: (analiseId) =>
    unwrap(api.get(`${BASE}/analises/${analiseId}/aprovadores`)),
  substituirAprovadores: (analiseId, aprovadores) =>
    unwrap(api.put(`${BASE}/analises/${analiseId}/aprovadores`, { aprovadores })),
  removerAprovador: (analiseId, aprovadorId) =>
    unwrap(api.delete(`${BASE}/analises/${analiseId}/aprovadores/${aprovadorId}`)),
  enviarAprovacao: (analiseId, body = {}) =>
    unwrap(api.post(`${BASE}/analises/${analiseId}/enviar-aprovacao`, body)),
  listarAprovacoes: (analiseId) =>
    unwrap(api.get(`${BASE}/analises/${analiseId}/aprovacoes`)),
  reenviarEmail: (aprovacaoId) =>
    unwrap(api.post(`${BASE}/aprovacoes/${aprovacaoId}/reenviar-email`)),
  forcarDecisao: (aprovacaoId, decisao, comentario = null) =>
    unwrap(api.post(`${BASE}/aprovacoes/${aprovacaoId}/forcar-decisao`, { decisao, comentario })),
};

export const aprovacaoPublicaAPI = {
  obter: (token) => unwrap(api.get(`${BASE}/publico/aprovacao/${token}`)),
  responder: (token, decisao, comentario = null) =>
    unwrap(api.post(`${BASE}/publico/aprovacao/${token}/responder`, { decisao, comentario })),
};

// ---------------- Projetos / fases / lançamentos / dependências ----------------

export const projetosAPI = {
  listar: (params = {}) => unwrap(api.get(`${BASE}/projetos`, { params })),
  obter: (id) => unwrap(api.get(`${BASE}/projetos/${id}`)),
  criar: (body) => unwrap(api.post(`${BASE}/projetos`, body)),
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/projetos/${id}`, body)),
  pausar: (id, motivo = null) =>
    unwrap(api.post(`${BASE}/projetos/${id}/pausar`, { motivo })),
  retomar: (id) => unwrap(api.post(`${BASE}/projetos/${id}/retomar`)),
  cancelar: (id, motivo) =>
    unwrap(api.post(`${BASE}/projetos/${id}/cancelar`, { motivo })),
  listarFases: (id) => unwrap(api.get(`${BASE}/projetos/${id}/fases`)),
  adicionarFase: (id, body) => unwrap(api.post(`${BASE}/projetos/${id}/fases`, body)),
  listarDependencias: (id) => unwrap(api.get(`${BASE}/projetos/${id}/dependencias`)),
  criarDependencia: (id, body) =>
    unwrap(api.post(`${BASE}/projetos/${id}/dependencias`, body)),
  removerDependencia: (id, body) =>
    unwrap(api.delete(`${BASE}/projetos/${id}/dependencias`, { data: body })),
  gantt: (id) => unwrap(api.get(`${BASE}/projetos/${id}/gantt`)),
  conflitos: (id) => unwrap(api.get(`${BASE}/projetos/${id}/conflitos`)),
  timeline: (id) => unwrap(api.get(`${BASE}/projetos/${id}/timeline`)),
  baseline: (id) => unwrap(api.get(`${BASE}/projetos/${id}/baseline`)),
  baselineComparacao: (id) => unwrap(api.get(`${BASE}/projetos/${id}/baseline/comparacao`)),
  custos: (id) => unwrap(api.get(`${BASE}/projetos/${id}/custos`)),
  criarCustoAdicional: (id, body) =>
    unwrap(api.post(`${BASE}/projetos/${id}/custos-adicionais`, body)),
  removerCustoAdicional: (custoId) =>
    unwrap(api.delete(`${BASE}/projetos/custos-adicionais/${custoId}`)),
};

export const adminCustosAPI = {
  listarTaxas: () => unwrap(api.get(`${BASE}/admin/custos/usuarios`)),
  definirTaxa: (usuarioId, valor_hora_brl) =>
    unwrap(api.put(`${BASE}/admin/custos/usuarios/${usuarioId}`, { valor_hora_brl })),
};

export const tarefasAPI = {
  listar: (faseId) => unwrap(api.get(`${BASE}/fases/${faseId}/tarefas`)),
  doProjeto: (projetoId) => unwrap(api.get(`${BASE}/projetos/${projetoId}/tarefas`)),
  gantt: (projetoId) => unwrap(api.get(`${BASE}/projetos/${projetoId}/tarefas-gantt`)),
  criar: (faseId, body) => unwrap(api.post(`${BASE}/fases/${faseId}/tarefas`, body)),
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/tarefas/${id}`, body)),
  deletar: (id) => unwrap(api.delete(`${BASE}/tarefas/${id}`)),
  addPredecessora: (id, predId) => unwrap(api.post(`${BASE}/tarefas/${id}/predecessoras`, { predecessora_id: predId })),
  removePredecessora: (id, predId) => unwrap(api.delete(`${BASE}/tarefas/${id}/predecessoras/${predId}`)),

  // Comentários (com @menção: mencionados = ids citados)
  listarComentarios: (id) => unwrap(api.get(`${BASE}/tarefas/${id}/comentarios`)),
  criarComentario: (id, texto, mencionados = []) =>
    unwrap(api.post(`${BASE}/tarefas/${id}/comentarios`, { texto, mencionados })),
  editarComentario: (cid, texto) => unwrap(api.patch(`${BASE}/tarefas/comentarios/${cid}`, { texto })),
  removerComentario: (cid) => unwrap(api.delete(`${BASE}/tarefas/comentarios/${cid}`)),

  // Anexos
  listarAnexos: (id) => unwrap(api.get(`${BASE}/tarefas/${id}/anexos`)),
  subirAnexo: (id, file) => {
    const fd = new FormData();
    fd.append('arquivo', file);
    return unwrap(api.post(`${BASE}/tarefas/${id}/anexos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }));
  },
  urlDownloadAnexo: (anexoId) => `${BASE}/tarefas/anexos/${anexoId}/download`,
  baixarAnexo: (anexoId) =>
    api.get(`${BASE}/tarefas/anexos/${anexoId}/download`, { responseType: 'blob' }),
  removerAnexo: (anexoId) => unwrap(api.delete(`${BASE}/tarefas/anexos/${anexoId}`)),

  // Checklist
  listarChecklist: (id) => unwrap(api.get(`${BASE}/tarefas/${id}/checklist`)),
  criarChecklist: (id, texto) => unwrap(api.post(`${BASE}/tarefas/${id}/checklist`, { texto })),
  atualizarChecklist: (itemId, body) => unwrap(api.patch(`${BASE}/tarefas/checklist/${itemId}`, body)),
  removerChecklist: (itemId) => unwrap(api.delete(`${BASE}/tarefas/checklist/${itemId}`)),
};

// Aprovação no nível do PROJETO (Onda B) — distinta da aprovação da análise.
export const projetoAprovacaoAPI = {
  listarAprovadores: (projetoId) => unwrap(api.get(`${BASE}/projetos/${projetoId}/aprovadores`)),
  substituirAprovadores: (projetoId, aprovadores) =>
    unwrap(api.put(`${BASE}/projetos/${projetoId}/aprovadores`, { aprovadores })),
  enviar: (projetoId) => unwrap(api.post(`${BASE}/projetos/${projetoId}/enviar-aprovacao`)),
  listarAprovacoes: (projetoId) => unwrap(api.get(`${BASE}/projetos/${projetoId}/aprovacoes`)),
  responder: (aprovacaoId, decisao, comentario = null) =>
    unwrap(api.post(`${BASE}/aprovacoes-projeto/${aprovacaoId}/responder`, { decisao, comentario })),
};

// Discussão da análise (comentários com @menção). Congela quando aprovada.
export const analiseComentariosAPI = {
  listar: (analiseId) => unwrap(api.get(`${BASE}/analises/${analiseId}/comentarios`)),
  criar: (analiseId, texto, mencionados = []) =>
    unwrap(api.post(`${BASE}/analises/${analiseId}/comentarios`, { texto, mencionados })),
  editar: (cid, texto) => unwrap(api.patch(`${BASE}/analises/comentarios/${cid}`, { texto })),
  remover: (cid) => unwrap(api.delete(`${BASE}/analises/comentarios/${cid}`)),
};

export const fasesAPI = {
  obter: (id) => unwrap(api.get(`${BASE}/fases/${id}`)),
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/fases/${id}`, body)),
  deletar: (id) => unwrap(api.delete(`${BASE}/fases/${id}`)),
  iniciar: (id) => unwrap(api.post(`${BASE}/fases/${id}/iniciar`)),
  concluir: (id) => unwrap(api.post(`${BASE}/fases/${id}/concluir`)),
  marcarRisco: (id, motivo = null) =>
    unwrap(api.post(`${BASE}/fases/${id}/marcar-risco`, { motivo })),
  bloquear: (id, motivo = null) =>
    unwrap(api.post(`${BASE}/fases/${id}/bloquear`, { motivo })),
  desbloquear: (id) => unwrap(api.post(`${BASE}/fases/${id}/desbloquear`)),
  listarLancamentos: (id) => unwrap(api.get(`${BASE}/fases/${id}/lancamentos`)),
  criarLancamento: (id, body) => unwrap(api.post(`${BASE}/fases/${id}/lancamentos`, body)),
  listarComentarios: (id) => unwrap(api.get(`${BASE}/fases/${id}/comentarios`)),
  criarComentario: (id, texto) =>
    unwrap(api.post(`${BASE}/fases/${id}/comentarios`, { texto })),
  timeline: (id) => unwrap(api.get(`${BASE}/fases/${id}/timeline`)),
};

export const lancamentosAPI = {
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/lancamentos/${id}`, body)),
  deletar: (id) => unwrap(api.delete(`${BASE}/lancamentos/${id}`)),
};

export const comentariosAPI = {
  atualizar: (id, texto) => unwrap(api.patch(`${BASE}/comentarios/${id}`, { texto })),
  deletar: (id) => unwrap(api.delete(`${BASE}/comentarios/${id}`)),
};

// "Meu trabalho" — endpoints centrados no usuario logado, cross-projeto.
export const meAPI = {
  fasesPendentes: () => unwrap(api.get(`${BASE}/me/fases`)),
  analisesParaResponder: () => unwrap(api.get(`${BASE}/me/analises-para-responder`)),
  notificacoes: (apenas_nao_lidas = false) =>
    unwrap(api.get(`${BASE}/me/notificacoes`, { params: { apenas_nao_lidas } })),
  contagemNaoLidas: () => unwrap(api.get(`${BASE}/me/notificacoes/contagem-nao-lidas`)),
  marcarLida: (id) => unwrap(api.post(`${BASE}/me/notificacoes/${id}/marcar-lida`)),
  marcarTodasLidas: () => unwrap(api.post(`${BASE}/me/notificacoes/marcar-todas-lidas`)),
};

// ---------------- Templates ----------------

export const templatesAPI = {
  listar: () => unwrap(api.get(`${BASE}/templates`)),
  obter: (id) => unwrap(api.get(`${BASE}/templates/${id}`)),
  criar: (body) => unwrap(api.post(`${BASE}/templates`, body)),
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/templates/${id}`, body)),
  desativar: (id) => unwrap(api.post(`${BASE}/templates/${id}/desativar`)),
  duplicar: (id) => unwrap(api.post(`${BASE}/templates/${id}/duplicar`)),
};

// ---------------- Gantt consolidado e conflitos ----------------

export const ganttAPI = {
  consolidado: (params) => unwrap(api.get(`${BASE}/gantt/consolidado`, { params })),
  conflitosAtuais: () => unwrap(api.get(`${BASE}/conflitos`)),
};

// ---------------- Retrospectiva e releases ----------------

export const retrospectivaAPI = {
  obter: (projetoId) => unwrap(api.get(`${BASE}/projetos/${projetoId}/retrospectiva`)),
  atualizar: (projetoId, body) =>
    unwrap(api.patch(`${BASE}/projetos/${projetoId}/retrospectiva`, body)),
  depoimentoPublico: (token, depoimento) =>
    unwrap(api.post(`${BASE}/publico/retrospectiva/${token}/depoimento`, { depoimento })),
};

export const releasesAPI = {
  listar: () => unwrap(api.get(`${BASE}/releases`)),
  obter: (id) => unwrap(api.get(`${BASE}/releases/${id}`)),
  criar: (body) => unwrap(api.post(`${BASE}/releases`, body)),
  atualizar: (id, body) => unwrap(api.patch(`${BASE}/releases/${id}`, body)),
  publicar: (id) => unwrap(api.post(`${BASE}/releases/${id}/publicar`)),
};

// ---------------- Dashboards ----------------

export const dashboardAPI = {
  apetiteSetores: (params = { periodo: 'ultimos_3_meses' }) =>
    unwrap(api.get(`${BASE}/dashboard/apetite-setores`, { params })),
  resumoGNI: () => unwrap(api.get(`${BASE}/dashboard/resumo-gni`)),
  cargaPessoas: (dias = 60) =>
    unwrap(api.get(`${BASE}/dashboard/carga-pessoas`, { params: { dias } })),
};

// ---------------- Admin ----------------

export const adminInovacaoAPI = {
  listarEventos: (params = {}) =>
    unwrap(api.get(`${BASE}/admin/eventos`, { params })),
  reprocessar: (id) =>
    unwrap(api.post(`${BASE}/admin/eventos/${id}/reprocessar`)),
  marcarIgnorado: (id) =>
    unwrap(api.post(`${BASE}/admin/eventos/${id}/marcar-ignorado`)),
  listarJobs: () => unwrap(api.get(`${BASE}/admin/jobs`)),
  executarJob: (id) => unwrap(api.post(`${BASE}/admin/jobs/${id}/executar`)),
  eventosDaEntidade: (entidade, entidadeId) =>
    unwrap(api.get(`${BASE}/admin/entidade/${entidade}/${entidadeId}/eventos`)),
};
